import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingBag,
  Clock,
  CheckCircle2,
  Truck,
  Package,
  XCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { beepWarning } from '@/lib/sounds';
import { GlassCard } from '@/components/ui/glass-card';
import { Button } from '@/components/ui/button';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  price: number;
  total: number;
  actualWeight?: number;
}

interface Order {
  id: string;
  status: string;
  customerName: string;
  customerPhone: string;
  customerAddress?: string;
  deliveryType: string;
  items: OrderItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  createdAt: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { key: 'todos', label: 'Todos' },
  { key: 'novos', label: 'Novos' },
  { key: 'em_preparo', label: 'Em preparo' },
  { key: 'prontos', label: 'Prontos' },
  { key: 'entregues', label: 'Entregues' },
];

const STATUS_LABELS: Record<string, string> = {
  aguardando_pagamento: 'Aguardando Pagamento',
  pago: 'Pago',
  aceito: 'Aceito',
  separando: 'Separando',
  pronto: 'Pronto',
  saiu_entrega: 'Saiu para Entrega',
  entregue: 'Entregue',
  cancelado: 'Cancelado',
};

const STATUS_COLORS: Record<string, string> = {
  aguardando_pagamento: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  pago: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  aceito: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  separando: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  pronto: 'bg-green-500/20 text-green-400 border-green-500/30',
  saiu_entrega: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  entregue: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  cancelado: 'bg-red-500/20 text-red-400 border-red-500/30',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h`;
  return `${Math.floor(diffHours / 24)}d`;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function OrdersPage() {
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState('todos');
  const [expandedOrder, setExpandedOrder] = React.useState<string | null>(null);
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = React.useState(true);
  const previousOrderCountRef = React.useRef<number>(0);

  // ─── Data Fetching ────────────────────────────────────────────────────────

  const fetchOrders = React.useCallback(async () => {
    try {
      const res = await apiFetch('/api/orders?source=online');
      if (res.ok) {
        const data = await res.json();
        const rawOrders = data.data || data.orders || data || [];
        const orderList: Order[] = rawOrders.map((o: Record<string, unknown>) => ({
          id: o.id as string,
          status: o.status as string,
          customerName: (o.customer_name || o.customerName) as string,
          customerPhone: (o.customer_phone || o.customerPhone) as string,
          customerAddress: (o.customer_address || o.customerAddress) as string | undefined,
          deliveryType: (o.delivery_type || o.deliveryType) as string,
          items: (o.items || []) as OrderItem[],
          subtotal: Number(o.subtotal),
          deliveryFee: Number(o.delivery_fee ?? o.deliveryFee ?? 0),
          total: Number(o.total),
          createdAt: (o.created_at || o.createdAt) as string,
        }));
        setOrders(orderList);

        // Sound notification for new orders
        if (
          soundEnabled &&
          previousOrderCountRef.current > 0 &&
          orderList.length > previousOrderCountRef.current
        ) {
          beepWarning();
          toast.info('Novo pedido recebido!', { icon: '🔔' });
        }
        previousOrderCountRef.current = orderList.length;
      }
    } catch {
      // Silently fail on refresh
    } finally {
      setLoading(false);
    }
  }, [soundEnabled]);

  // Initial fetch
  React.useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Auto-refresh every 15 seconds
  React.useEffect(() => {
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // ─── Filtered Orders ──────────────────────────────────────────────────────

  const filteredOrders = React.useMemo(() => {
    switch (activeTab) {
      case 'novos':
        return orders.filter((o) => ['aguardando_pagamento', 'pago'].includes(o.status));
      case 'em_preparo':
        return orders.filter((o) => ['aceito', 'separando'].includes(o.status));
      case 'prontos':
        return orders.filter((o) => ['pronto', 'saiu_entrega'].includes(o.status));
      case 'entregues':
        return orders.filter((o) => o.status === 'entregue');
      default:
        return orders;
    }
  }, [orders, activeTab]);

  // ─── Actions ──────────────────────────────────────────────────────────────

  async function updateOrderStatus(orderId: string, newStatus: string) {
    setActionLoading(orderId);
    try {
      const res = await apiFetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });

      if (res.ok) {
        toast.success(`Pedido atualizado para: ${STATUS_LABELS[newStatus] || newStatus}`);
        await fetchOrders();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao atualizar pedido');
      }
    } catch {
      toast.error('Erro ao atualizar pedido');
    } finally {
      setActionLoading(null);
    }
  }

  function getActions(order: Order): { label: string; status: string; variant?: string }[] {
    const actions: { label: string; status: string; variant?: string }[] = [];

    switch (order.status) {
      case 'aguardando_pagamento':
        actions.push({ label: 'Confirmar Pagamento', status: 'pago' });
        break;
      case 'pago':
        actions.push({ label: 'Aceitar Pedido', status: 'aceito' });
        break;
      case 'aceito':
        actions.push({ label: 'Iniciar Separação', status: 'separando' });
        break;
      case 'separando':
        actions.push({ label: 'Marcar como Pronto', status: 'pronto' });
        break;
      case 'pronto':
        actions.push({ label: 'Saiu para Entrega', status: 'saiu_entrega' });
        break;
      case 'saiu_entrega':
        actions.push({ label: 'Marcar como Entregue', status: 'entregue' });
        break;
    }

    if (!['entregue', 'cancelado'].includes(order.status)) {
      actions.push({ label: 'Cancelar', status: 'cancelado', variant: 'destructive' });
    }

    return actions;
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Pedidos Online</h1>
          <p className="text-base text-muted-foreground">
            {orders.length} pedido{orders.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSoundEnabled(!soundEnabled)}
            title={soundEnabled ? 'Desativar som' : 'Ativar som'}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchOrders}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {STATUS_TABS.map((tab) => {
          let count = 0;
          switch (tab.key) {
            case 'novos':
              count = orders.filter((o) => ['aguardando_pagamento', 'pago'].includes(o.status)).length;
              break;
            case 'em_preparo':
              count = orders.filter((o) => ['aceito', 'separando'].includes(o.status)).length;
              break;
            case 'prontos':
              count = orders.filter((o) => ['pronto', 'saiu_entrega'].includes(o.status)).length;
              break;
            case 'entregues':
              count = orders.filter((o) => o.status === 'entregue').length;
              break;
            default:
              count = orders.length;
          }
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-base font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground border border-border'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span className={`ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-xs font-bold ${
                  tab.key === 'novos' ? 'bg-destructive text-destructive-foreground' : 'bg-muted-foreground/20 text-foreground'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <GlassCard className="flex flex-col items-center justify-center py-12">
          <ShoppingBag className="h-12 w-12 text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">Nenhum pedido encontrado</p>
        </GlassCard>
      ) : (
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {filteredOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                expanded={expandedOrder === order.id}
                onToggle={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                actions={getActions(order)}
                onAction={(status) => updateOrderStatus(order.id, status)}
                actionLoading={actionLoading === order.id}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Auto-refresh indicator */}
      <p className="text-center text-xs text-muted-foreground/60">
        Atualização automática a cada 15 segundos
      </p>
    </div>
  );
}

// ─── Order Card ───────────────────────────────────────────────────────────────

function OrderCard({
  order,
  expanded,
  onToggle,
  actions,
  onAction,
  actionLoading,
}: {
  order: Order;
  expanded: boolean;
  onToggle: () => void;
  actions: { label: string; status: string; variant?: string }[];
  onAction: (status: string) => void;
  actionLoading: boolean;
}) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <GlassCard className="p-0 overflow-hidden">
        {/* Summary row */}
        <button
          onClick={onToggle}
          className="flex w-full items-center gap-3 p-4 text-left hover:bg-accent/30 transition-colors"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-base font-bold text-foreground">
                #{order.id.slice(-6)}
              </span>
              <span
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                  STATUS_COLORS[order.status] || 'bg-muted text-muted-foreground'
                }`}
              >
                {STATUS_LABELS[order.status] || order.status}
              </span>
            </div>
            <p className="text-base text-muted-foreground truncate">{order.customerName}</p>
          </div>

          <div className="text-right shrink-0">
            <p className="text-base font-bold text-foreground">{formatCurrency(order.total)}</p>
            <p className="text-sm text-muted-foreground flex items-center gap-1 justify-end">
              <Clock className="h-3 w-3" />
              {timeAgo(order.createdAt)}
            </p>
          </div>

          {expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
        </button>

        {/* Expanded details */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="border-t border-border px-4 py-3 space-y-3">
                {/* Customer info */}
                <div className="text-base space-y-1">
                  <p className="text-muted-foreground">
                    📱 {order.customerPhone}
                  </p>
                  {order.customerAddress && (
                    <p className="text-muted-foreground">
                      📍 {order.customerAddress}
                    </p>
                  )}
                  <p className="text-muted-foreground">
                    {order.deliveryType === 'entrega' ? '🛵 Entrega' : '🏪 Retirada'}
                  </p>
                </div>

                {/* Items */}
                <div className="rounded-lg bg-muted/30 p-3 space-y-1.5">
                  {order.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-base">
                      <span className="text-foreground">
                        {item.quantity}{item.unit === 'kg' ? 'kg' : 'x'} {item.name}
                      </span>
                      <span className="text-muted-foreground font-medium">
                        {formatCurrency(item.total)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-border/50 pt-1.5 mt-1.5">
                    <div className="flex justify-between text-base">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="text-foreground">{formatCurrency(order.subtotal)}</span>
                    </div>
                    {order.deliveryFee > 0 && (
                      <div className="flex justify-between text-base">
                        <span className="text-muted-foreground">Entrega</span>
                        <span className="text-foreground">{formatCurrency(order.deliveryFee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-bold">
                      <span className="text-foreground">Total</span>
                      <span className="text-foreground">{formatCurrency(order.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {actions.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {actions.map((action) => (
                      <Button
                        key={action.status}
                        variant={action.variant === 'destructive' ? 'destructive' : 'default'}
                        size="sm"
                        disabled={actionLoading}
                        onClick={() => onAction(action.status)}
                      >
                        {actionLoading ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : null}
                        {action.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </GlassCard>
    </motion.div>
  );
}
